import { readEnv } from "@repo/helpers";
import { differenceInMinutes } from "date-fns";
import { all, put, select, takeEvery } from "typed-redux-saga";
import { StepperIndexes } from "../../stepperIndexes";
import {
    buildStepperState,
    changePensionContributionPrerequisiteSteps,
    pensionContributionQuestionnaireSteps,
    pensionQuestionnaireInvestmentStrategySteps,
    pensionQuestionnaireSteps,
    pensionQuestionnaireStepsWithoutIntro,
} from "../helpers/stepperStateBuilder";
import { RootState } from "../rootStore";

/** Default questionnaire validity period (48 hours) when env var is not set */
const DEFAULT_QUESTIONNAIRE_VALIDITY_MINUTES = 2880;

import {
    pensionContributionChangeSelector,
    questionnaireSubmitDateSelector,
    stepIndexByNameSelector,
    stepperTypeSelector,
} from "../slices";
import {
    initializeQuestionnaire,
    initializeStepperByName,
    insertStepsIfMissing,
    removeStepsIfPresent,
    setChangeContributionState,
    setLastQuestionnaireSubmitDate,
    StepperType,
} from "../slices/stepperSlice";

function* initSteps(action: ReturnType<typeof initializeStepperByName>) {
    //get current stepper state
    const state = yield* select((state: RootState) => state.stepper);
    //do nothing if steps are already initialized
    if (state.allStepsList.length > 0) return;

    // Contract context is mandatory for questionnaire steppers.
    // If missing, initialize into an explicit error state so UI can render
    // a dedicated stepper error page instead of crashing.
    const contractId = action.payload.contractId?.trim() ?? "";
    if (!contractId) {
        yield* put(initializeQuestionnaire(buildStepperState([], action.payload.name, "", "missingContractId")));
        return;
    }

    if (action.payload.name === StepperType.PensionQuestionnaire) {
        // initial setup of steps
        yield* put(
            initializeQuestionnaire(
                buildStepperState(pensionQuestionnaireSteps, StepperType.PensionQuestionnaire, contractId),
            ),
        );
    } else if (action.payload.name === StepperType.ChangeInvestmentStrategy) {
        yield* put(
            initializeQuestionnaire(
                buildStepperState(
                    pensionQuestionnaireInvestmentStrategySteps,
                    StepperType.ChangeInvestmentStrategy,
                    contractId,
                ),
            ),
        );
    } else if (action.payload.name === StepperType.ChangePensionContribution) {
        const { useAml, useBankId } = action.payload;
        // wait until useAml and useBankId are defined, dont proceed if they are undefined
        if (useAml === undefined || useBankId === undefined) return;
        // initial setup of steps
        yield* put(
            initializeQuestionnaire(
                buildStepperState(
                    [
                        ...changePensionContributionPrerequisiteSteps(useBankId, useAml),
                        ...pensionContributionQuestionnaireSteps,
                    ],
                    StepperType.ChangePensionContribution,
                    contractId,
                ),
            ),
        );
    }
}

function* updatePensionContributionSteps() {
    //Get stepper type
    const stepperType = yield* select(stepperTypeSelector);
    //Get last questionnaire submit date
    const lastSubmitDate = yield* select(questionnaireSubmitDateSelector);
    //Only proceed if we are in change pension contribution stepper
    if (stepperType !== StepperType.ChangePensionContribution) return;
    //Only proceed if last submit date is loaded (neverSubmitted or submitted)
    if (lastSubmitDate.status === "notLoaded") return;
    //Get what user changed in the change pension contribution flow
    const change = yield* select(pensionContributionChangeSelector);
    //Get step index of changes overview (after which we may need to insert steps)
    const overviewPageIndex = yield* select(stepIndexByNameSelector, StepperIndexes.changeContributionOverviewPage);

    //Calculate minutes since last submission
    const now = new Date();
    const submittedBeforeMinutes = lastSubmitDate.date
        ? differenceInMinutes(now, new Date(lastSubmitDate.date))
        : Number.POSITIVE_INFINITY;
    const configuredValidity = Number(readEnv("VITE_CONTRIBUTION_CHANGE_QUESTIONNAIRE_VALIDITY_MINUTES"));
    const validityMinutes = configuredValidity || DEFAULT_QUESTIONNAIRE_VALIDITY_MINUTES;

    //Update steps based on what user changed
    if (change.contributionChanged && submittedBeforeMinutes > validityMinutes) {
        yield* put(
            insertStepsIfMissing({
                steps: pensionQuestionnaireStepsWithoutIntro,
                index: overviewPageIndex + 1,
            }),
        );
    } else {
        // Don't remove questionnaire steps while the user is currently on one of them.
        // A background refetch of pensionDetail can re-trigger this saga; removing steps
        // mid-flow causes stepIndex to clamp to the end, crashing the user onto RequestSentPage.
        const { stepIndex, allStepsList } = yield* select((state: RootState) => state.stepper);
        const currentStepKey = allStepsList[stepIndex];
        const userIsOnQuestionnaireStep = pensionQuestionnaireStepsWithoutIntro.indexOf(currentStepKey) !== -1;
        if (!userIsOnQuestionnaireStep) {
            yield* put(removeStepsIfPresent(pensionQuestionnaireStepsWithoutIntro));
        }
    }
}

export function* stepperSaga() {
    yield* all([
        takeEvery(initializeStepperByName.type, initSteps),
        // Trigger step updates whenever the user changes contribution state
        takeEvery(
            [setChangeContributionState.type, setLastQuestionnaireSubmitDate.type],
            updatePensionContributionSteps,
        ),
    ]);
}
